import React, { useState } from 'react'
import { SKILL_LEVELS } from '../../../constants/skillLevels'
import { isValidSkillLevel } from '../../../utils/validateSkillLevel'
import useKeyboardNavigation from '../../../hooks/useKeyboardNavigation'
import {
  NTRP_OVERVIEW,
  getNTRPDescription,
} from '../../../constants/ntrpDescriptions'
import InfoTooltip from '../../common/InfoTooltip'

function SelectSkillLevelStep({
  selectedSkillLevel,
  onSelectSkillLevel,
  onNext,
  onBack,
  activeLeagueLevels = [],
  isLoadingLeagueLevels = false,
  partnerSkillLevel = null,
  isDoublesRegistration = false,
  requiresPayment = false,
}) {
  const [showDescriptions, setShowDescriptions] = useState(false)
  const hasLevelFilter = activeLeagueLevels.length > 0
  const availableSkillLevels = hasLevelFilter
    ? SKILL_LEVELS.filter((level) =>
        activeLeagueLevels.some(
          (leagueLevel) =>
            Number(level.value) === Number(leagueLevel.skill_level),
        ),
      )
    : SKILL_LEVELS
  const hasValidSelection =
    isValidSkillLevel(selectedSkillLevel) &&
    (!hasLevelFilter ||
      availableSkillLevels.some((l) => l.value === selectedSkillLevel))

  // Doubles skill level floor: cannot select below partner's doubles_skill_level
  const skillFloor =
    partnerSkillLevel != null ? Number(partnerSkillLevel) : null

  // Enable keyboard navigation for radiogroup accessibility
  const { onRadioKeyDown, radiogroupRef } = useKeyboardNavigation(
    availableSkillLevels,
    selectedSkillLevel,
    onSelectSkillLevel,
    skillFloor != null ? (level) => level.value >= skillFloor : null,
    { getValueFromItem: (level) => level.value },
  )

  const stepNumber = isDoublesRegistration ? 3 : 2
  const nextLabel = isDoublesRegistration
    ? requiresPayment
      ? 'Next: Payment'
      : 'Next: Review'
    : 'Next: Choose Court'

  return (
    <div className="registration-step">
      <h4 className="step-title">
        Step {stepNumber}: Select Your Skill Level
        <InfoTooltip
          content="Use your official USTA NTRP rating if you have one. If you don't have an official rating, select the level that best matches your playing ability based on the NTRP descriptions below."
          placement="bottom"
        />
      </h4>
      <p className="step-description">
        Choose your NTRP (National Tennis Rating Program) skill level. This
        helps us match you with players of similar ability.
      </p>

      <div className="ntrp-info-box">
        <div className="ntrp-info-header">
          <div>
            <strong>About NTRP</strong>
            <p className="ntrp-info-text">{NTRP_OVERVIEW.description}</p>
          </div>
          <button
            type="button"
            className="btn btn-link ntrp-toggle-btn"
            onClick={() => setShowDescriptions(!showDescriptions)}
            aria-expanded={showDescriptions}
            aria-controls="ntrp-descriptions-region"
            aria-label={
              showDescriptions
                ? 'Hide rating descriptions'
                : 'Show rating descriptions'
            }
          >
            {showDescriptions ? 'Hide Details' : 'Show Level Details'}
          </button>
        </div>

        {showDescriptions && (
          <div
            id="ntrp-descriptions-region"
            className="ntrp-descriptions"
            role="region"
            aria-live="polite"
            aria-label="NTRP rating level descriptions"
          >
            <h5 className="ntrp-descriptions-title">
              Rating Level Descriptions
            </h5>
            <div className="ntrp-descriptions-list">
              {availableSkillLevels.map((level) => {
                const description = getNTRPDescription(level.value)
                // Defensive check: All SKILL_LEVELS (2.5-6.0) have descriptions
                // in NTRP_LEVEL_DESCRIPTIONS, but this guards against configuration errors
                if (!description) return null

                return (
                  <div key={level.value} className="ntrp-description-item">
                    <div className="ntrp-description-header">
                      <span className="ntrp-description-level">
                        {level.label}
                      </span>
                      <span className="ntrp-description-title">
                        {description.title}
                      </span>
                    </div>
                    <p className="ntrp-description-text">
                      {description.description}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {isLoadingLeagueLevels ? (
        <p className="text-muted small mt-2 mb-0" role="status">
          Loading available skill levels...
        </p>
      ) : (
        <div
          ref={radiogroupRef}
          className="skill-level-grid"
          role="radiogroup"
          aria-label="Skill level selection"
        >
          {availableSkillLevels.map((level) => {
            const isSelected = selectedSkillLevel === level.value
            const ntrpInfo = getNTRPDescription(level.value)
            const isBelowFloor = skillFloor != null && level.value < skillFloor

            return (
              <button
                key={level.value}
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-disabled={isBelowFloor || undefined}
                className={`skill-level-card ${isSelected ? 'selected' : ''} ${isBelowFloor ? 'opacity-50' : ''}`}
                onClick={() => !isBelowFloor && onSelectSkillLevel(level.value)}
                onKeyDown={onRadioKeyDown}
              >
                <div className="skill-level-value">{level.label}</div>
                {ntrpInfo && (
                  <div className="skill-level-category">{ntrpInfo.title}</div>
                )}
              </button>
            )
          })}
        </div>
      )}

      {skillFloor != null && (
        <p className="text-muted small mt-2">
          Your partner&apos;s doubles rating is {skillFloor}. Your team&apos;s
          skill level must be at least {skillFloor}.
        </p>
      )}

      <div className="sr-only" aria-live="polite" role="status">
        {hasValidSelection ? `Selected skill level: ${selectedSkillLevel}` : ''}
      </div>

      <div className="step-navigation">
        <button
          className="btn btn-outline-secondary"
          onClick={onBack}
          aria-label={
            isDoublesRegistration
              ? 'Go back to court and partner selection'
              : 'Go back to league selection'
          }
        >
          Back
        </button>
        <button
          className="btn btn-primary btn-lg flex-grow-1"
          onClick={onNext}
          disabled={!hasValidSelection || isLoadingLeagueLevels}
          aria-label={
            isDoublesRegistration
              ? requiresPayment
                ? 'Continue to payment'
                : 'Continue to review'
              : 'Continue to court selection'
          }
        >
          {nextLabel}
        </button>
      </div>
    </div>
  )
}

export default SelectSkillLevelStep
